import { HttpError, NetworkError } from '@luminovo/api-contracts';
import { id, isChunkLoadError, logToExternalErrorHandlers, normalizePathname } from '@luminovo/commons';
import { analytics } from '@luminovo/data-stack';
import { CenteredLayout, useNavigate } from '@luminovo/design-system';
import * as Sentry from '@sentry/react';
import { type JSX, useEffect, useRef } from 'react';

import { useTenantDetailsContext } from '@/shared/data/contexts/TenantDetailsContext';
import { pageToReturnToAfterSignIn } from '@/shared/utils/loginRedirect';

import { NotAuthorizedPage } from '../../permissions/NotAuthorizedPage';
import { useOnCookieConsent } from '../../utils/services';
import { initializeSentry } from '../../utils/services';
import { isAppStale } from '../../utils/staleApp';
import { ResourceNotFound } from '../NotFoundCard';
import { ErrorCodeProblem, NetworkConnectionProblem, NewVersionAvailable } from './NonIdealStates';
import { SadBox } from './SadBox';

export function ErrorFallback({ error, onLogOut }: { error: unknown; onLogOut?: () => void }) {
    // Ensure that Sentry is initialized before we call logToExternalErrorHandlers
    const consentedCookies = useOnCookieConsent();
    const { tenantDetails } = useTenantDetailsContext();
    initializeSentry({ consentedCookies, tenantName: tenantDetails.tenant });

    // While stale, any error is treated as a lost chunk: a lost import resolves to `undefined` and fails elsewhere.
    const isLostChunk = isChunkLoadError(error) || isAppStale();

    // In an effect: the fallback re-renders on every context change, which would count the failure again.
    useEffect(() => {
        if (isLostChunk) {
            logChunkLoadError(error instanceof Error ? error : new Error(String(error)));
        }
    }, [error, isLostChunk]);

    if (error instanceof NetworkError) {
        return <NetworkConnectionProblem />;
    }

    // Before the HttpError guard below, which returns early for the plain TypeError a lost chunk throws.
    if (isLostChunk) {
        return <NewVersionAvailable />;
    }

    if (!(error instanceof HttpError)) {
        return <ReportedSadBox error={error} onLogOut={onLogOut} />;
    }

    if (error.code && error.code !== 'unknown') {
        return <ErrorCodeProblem code={error.code} onLogOut={onLogOut} />;
    }

    // The boundary caught a 401 before the token refresh and won't retry: the login page brings the user back here.
    if (error.status === 401) {
        return <ExpiredSessionRedirect />;
    }

    if (error.status === 403) {
        return <NotAuthorizedPage />;
    }

    if (error.status === 404) {
        return (
            <CenteredLayout>
                <ResourceNotFound endpoint={error.endpoint} />
            </CenteredLayout>
        );
    }

    // No fingerprint: HttpError carries its own, so a failing endpoint stays one issue wherever it was caught.
    return <ReportedSadBox error={error} onLogOut={onLogOut} />;
}

/** Sends the user to the email form, which returns them to the page they were on, once per mount. */
function ExpiredSessionRedirect(): null {
    const navigate = useNavigate();
    const sent = useRef(false);

    useEffect(() => {
        // StrictMode runs a new component's effects twice.
        if (sent.current) {
            return;
        }
        sent.current = true;
        // Read once, for both `redirectTo` and the event.
        const page = window.location.pathname + window.location.search;
        const redirectTo = pageToReturnToAfterSignIn(page);
        // No event on a sign-in page: the provider's redirect there is on its way and counted the lost session.
        if (redirectTo === page) {
            // Before navigating, while Segment still reads the page off the address bar.
            analytics.track(id('auth/email_form_redirected'), { source: 'error_fallback' });
        }
        // Like `AuthenticatedLayout`'s lost-session redirect: replace, and skip the unsaved-changes guard, since every
        // request fails now and "Stay on this page" would only trap the user.
        // oxlint-disable-next-line routing/no-navigate-in-effect -- a lost session ends every page; redirectTo is the address bar's URL
        navigate({ to: '/auth/enter-email', search: { redirectTo }, replace: true, ignoreBlocker: true });
    }, [navigate]);

    return null;
}

/** Reports from an effect, so a route boundary's `componentDidCatch` report, with the component stack, goes first. */
function ReportedSadBox({ error, onLogOut }: { error: unknown; onLogOut?: () => void }): JSX.Element {
    useEffect(() => {
        logToExternalErrorHandlers(error instanceof Error ? error : new Error('Error without error message.'));
    }, [error]);

    return <SadBox onLogOut={onLogOut} />;
}

/** A named log, not an exception: per-chunk TypeErrors make a new Sentry group every deploy and can't be trended. */
function logChunkLoadError(error: Error): void {
    // How often a deploy costs someone their work, which decides whether old assets outlive a release.
    Sentry.logger.warn('app.chunk_load_failed', {
        error: error.message,
        // The chunk the browser could not fetch, as reported inside the message.
        module_url: /(https?:\/\/\S+)/.exec(error.message)?.[1] ?? '',
        pathname: normalizePathname(),
        luminovo_version: window.LUMINOVO_VERSION || 'unknown',
        // How long the tab ran the stale build; short values mean assets vanish while people work.
        page_age_ms: Math.round(performance.now()),
    });
}
